import {
  type CurrencyCode,
  PayPointMode,
  type PayPointSettingsDto,
  PayPointType,
} from "Infrastructure/Api/Api";

// The merchant-facing "payment type" radio. Each option maps onto a BE mode plus the
// settings that mode carries; everything else in this module is derived from that table.
export const PaymentType = [
  "Standard",
  "EditablePrice",
  "FixedPrice",
  "NfcLink",
  "NfcTableService",
] as const;
export type PaymentTypeValue = (typeof PaymentType)[number];

const MODE_BY_PAYMENT_TYPE = {
  Standard: { mode: PayPointMode.Standard, usesFixedAmount: false },
  EditablePrice: { mode: PayPointMode.Collector, usesFixedAmount: false },
  FixedPrice: { mode: PayPointMode.Collector, usesFixedAmount: true },
  NfcLink: { mode: PayPointMode.Link, usesFixedAmount: false },
  NfcTableService: { mode: PayPointMode.TableService, usesFixedAmount: false },
} as const satisfies Record<
  PaymentTypeValue,
  { mode: PayPointMode; usesFixedAmount: boolean }
>;

const isPaymentTypeValue = (value: string): value is PaymentTypeValue =>
  value in MODE_BY_PAYMENT_TYPE;

const isRedirectMode = (mode: PayPointMode) =>
  mode === PayPointMode.Link || mode === PayPointMode.TableService;

export const isCollectorPaymentType = (paymentType: PaymentTypeValue) =>
  MODE_BY_PAYMENT_TYPE[paymentType].mode === PayPointMode.Collector;

// Takes the raw string a radio group emits so callers don't have to cast it first.
export const isRedirectPaymentType = (paymentType: string) =>
  isPaymentTypeValue(paymentType) &&
  isRedirectMode(MODE_BY_PAYMENT_TYPE[paymentType].mode);

// Mirrors PayPointSettings.RedirectUrlMaxLength on the BE.
export const REDIRECT_URL_MAX_LENGTH = 2048;

// Mirrors the BE rule: absolute https URL without user info.
export const isValidRedirectUrl = (value: string | null | undefined) => {
  try {
    const url = new URL(value ?? "");
    return url.protocol === "https:" && !url.username && !url.password;
  } catch {
    return false;
  }
};

export type PaymentSettingsFormValues = {
  paymentType: PaymentTypeValue;
  fixedAmount?: number | null;
  tipsEnabled: boolean;
  currencyCode?: CurrencyCode | null;
  redirectUrl?: string | null;
};

export const toPayPointSettingsDto = ({
  type,
  paymentType,
  fixedAmount,
  tipsEnabled,
  currencyCode,
  redirectUrl,
}: PaymentSettingsFormValues & { type: PayPointType }): PayPointSettingsDto => {
  // Gateway paypoints have no merchant-side payment settings — the e-shop drives the
  // amount and handles tips itself per transaction — so the neutral default is persisted
  // regardless of what the (disabled) form fields hold.
  if (type === PayPointType.Gateway) {
    return {
      mode: PayPointMode.Standard,
      predefinedAmount: null,
      predefinedAmountCurrencyCode: null,
      tipsEnabled: false,
      redirectUrl: null,
    };
  }

  const { mode, usesFixedAmount } = MODE_BY_PAYMENT_TYPE[paymentType];

  return {
    mode,
    predefinedAmount: usesFixedAmount ? (fixedAmount ?? null) : null,
    // The BE requires a currency on every Collector paypoint (fixed and
    // editable price alike) and forbids it on the other modes.
    predefinedAmountCurrencyCode:
      mode === PayPointMode.Collector ? (currencyCode ?? null) : null,
    tipsEnabled,
    // The Table service URL is owned by the Hub.
    redirectUrl: mode === PayPointMode.Link ? redirectUrl : null,
  };
};

// defaultCurrencyCode is the merchant's market currency; an undefined value
// (region-less context) leaves the currency empty and the form validation
// forces an explicit choice.
export const fromPayPointSettingsDto = (
  settings: PayPointSettingsDto | null | undefined,
  defaultCurrencyCode: CurrencyCode | undefined,
): PaymentSettingsFormValues => {
  if (!settings) {
    return {
      paymentType: "Standard",
      fixedAmount: null,
      tipsEnabled: false,
      currencyCode: defaultCurrencyCode,
    };
  }

  const {
    mode,
    predefinedAmount,
    predefinedAmountCurrencyCode,
    tipsEnabled,
    redirectUrl,
  } = settings;

  if (mode === PayPointMode.Collector) {
    const hasPredefinedAmount = predefinedAmount != null;
    return {
      paymentType: hasPredefinedAmount ? "FixedPrice" : "EditablePrice",
      fixedAmount: predefinedAmount ?? null,
      tipsEnabled,
      currencyCode: predefinedAmountCurrencyCode ?? defaultCurrencyCode,
    };
  }

  return {
    paymentType:
      PaymentType.find((type) => MODE_BY_PAYMENT_TYPE[type].mode === mode) ??
      "Standard",
    fixedAmount: null,
    tipsEnabled,
    currencyCode: defaultCurrencyCode,
    redirectUrl,
  };
};
